// Trade store - Svelte 5 reactive state for trade mode
// Manages trade lifecycle, binder caching, and real-time sync flags

import { setTradeFlags } from '../services/trade-service.js';

// ============================================================
// Reactive state
// ============================================================

export const tradeState = $state({
  mode: 'normal', // 'normal' | 'trade'
  currentTradeId: null, // Active trade Firestore document ID
  currentTrade: null, // Full trade document data from Firestore
  userSide: null, // 'A' | 'B' (which side the current user owns)
  viewingSide: null, // 'A' | 'B' (which binder is currently displayed)

  // Firestore listener unsubscribe function (not reactive, just stored)
  tradeUnsubscribe: null,

  // Sync flags for suppressing notifications and optimizing re-renders
  pendingLocalChange: false, // Set before Firestore writes to suppress self-notifications
  pendingExchangeUpdate: false, // Skip full re-render, only update exchange checkboxes
  applyingRemoteUpdate: false, // Prevents sync loops during selection application

  // Previous trade snapshot for change detection
  previousTradeSnapshot: null,

  // Binder cache for instant side-swapping
  // Each side stores: { url, allCardsData, binderColor, currentPage, sortMode,
  //                     isCollection, collectionType, totalCards, ... }
  binderCache: { A: null, B: null },
});

// ============================================================
// Connect reactive flags to trade-service.js
// ============================================================

setTradeFlags({
  setPendingLocalChange: (v) => {
    tradeState.pendingLocalChange = v;
  },
  setPendingExchangeUpdate: (v) => {
    tradeState.pendingExchangeUpdate = v;
  },
});

// ============================================================
// Trade mode lifecycle
// ============================================================

/**
 * Enter trade mode
 * @param {string} tradeId - Firestore trade document ID
 * @param {'A'|'B'} userSide - Which side the current user owns
 */
export function enterTradeMode(tradeId, userSide) {
  tradeState.mode = 'trade';
  tradeState.currentTradeId = tradeId;
  tradeState.userSide = userSide;
  tradeState.viewingSide = userSide === 'A' ? 'B' : 'A'; // Start viewing the other side
  tradeState.pendingLocalChange = false;
  tradeState.pendingExchangeUpdate = false;
  tradeState.applyingRemoteUpdate = false;
  tradeState.previousTradeSnapshot = null;
  tradeState.binderCache = { A: null, B: null };
  console.log('[Trade Store] Entered trade mode:', tradeId, 'as side', userSide);
}

/**
 * Exit trade mode and clean up
 */
export function exitTradeMode() {
  // Unsubscribe from Firestore listener
  if (tradeState.tradeUnsubscribe) {
    tradeState.tradeUnsubscribe();
  }

  tradeState.mode = 'normal';
  tradeState.currentTradeId = null;
  tradeState.currentTrade = null;
  tradeState.userSide = null;
  tradeState.viewingSide = null;
  tradeState.tradeUnsubscribe = null;
  tradeState.pendingLocalChange = false;
  tradeState.pendingExchangeUpdate = false;
  tradeState.applyingRemoteUpdate = false;
  tradeState.previousTradeSnapshot = null;
  tradeState.binderCache = { A: null, B: null };
  console.log('[Trade Store] Exited trade mode');
}

// ============================================================
// Side management
// ============================================================

/**
 * Set which binder side is currently being viewed
 */
export function setViewingSide(side) {
  tradeState.viewingSide = side;
}

/**
 * Cache binder data for a side (for instant swapping)
 * @param {'A'|'B'} side
 * @param {Object} data - Binder data to cache
 */
export function cacheBinder(side, data) {
  tradeState.binderCache[side] = data;
}

/**
 * Get cached binder data for a side
 * @param {'A'|'B'} side
 * @returns {Object|null}
 */
export function getCachedBinder(side) {
  return tradeState.binderCache[side];
}

// ============================================================
// Derived helpers
// ============================================================

/**
 * Check if trade is finalized (both parties accepted)
 */
export function isFinalized() {
  const trade = tradeState.currentTrade;
  return trade ? trade.acceptedByA && trade.acceptedByB : false;
}

/**
 * Check if trade is complete
 */
export function isComplete() {
  const trade = tradeState.currentTrade;
  return trade ? trade.status === 'complete' : false;
}

/**
 * Check if currently in trade mode
 */
export function isTradeMode() {
  return tradeState.mode === 'trade';
}

/**
 * Get the label for a side relative to the current user
 * @param {'A'|'B'} side
 * @returns {'Yours'|'Theirs'}
 */
export function getSideLabel(side) {
  return side === tradeState.userSide ? 'Yours' : 'Theirs';
}

/**
 * Get the side data for a given side from the current trade
 * @param {'A'|'B'} side
 * @returns {Object|null}
 */
export function getSideData(side) {
  const trade = tradeState.currentTrade;
  if (!trade) return null;
  return side === 'A' ? trade.sideA : trade.sideB;
}

/**
 * Get the "actual" trade side for the currently viewed binder.
 * When viewing the OTHER user's binder, cards you select go on YOUR side.
 * This returns the side whose cards[] array corresponds to the viewed binder.
 */
export function getViewedTradeSide() {
  // If viewing side B (the other's binder), cards are on side A (your selections from their binder)
  // If viewing side A (your own binder), cards are on side B (their selections from your binder)
  return tradeState.viewingSide;
}
